// Subject and the opening message.
//
// `is_internal_note` is the most consequential field on the screen: an internal
// technical note reaching a customer is the failure mode that matters (SPEC.md
// §7.4), and collaborators — hardware and implementation — can now send outward.
// So the two modes do not share a look. The internal note is amber and says it is
// not sent; the public reply is blue and says it is emailed. No icon-only toggle,
// no colour-only difference, and the mode is stated again next to the submit
// button before anything is sent.
//
// The default is an internal note, matching `initial_message.is_internal_note`
// defaulting to true on the API: a phone log is a note about a call, not a message
// to the customer (docs/API.md §3.3).
//
// THEMING (SPEC.md §7.4). Every surface that answers "is this internal?" is built
// from the note/reply tokens in src/styles/app.css, never from a neutral, and never
// from a raw amber/blue step — so the distinction is one decision made in one place
// and it gets LOUDER in dark, where untreated greys converge:
//
//   mode toggle   note pressed  = bg-note-accent + text-note-on-accent (7.0:1 light,
//                                 8.7:1 dark) — a filled amber chip, identical in
//                                 both themes, matching the pre-submit chip in
//                                 New.jsx so the same fill means the same thing at
//                                 both ends of the form.
//                 reply pressed = bg-reply-strong + text-reply-ink (9.6:1 / 11.1:1),
//                                 a cool tint, deliberately a lighter treatment than
//                                 the note's full fill.
//   message well  note  = 8px bg-note-accent left bar, bg-note body, ring-note-line
//                         (ring-2 in dark, per RULE 3), text-note-ink label.
//                 reply = 8px bg-reply-accent left bar, bg-reply-strong body,
//                         ring-reply-accent, text-reply-ink label.
//   the textarea  itself is themed too, and this is the part that matters most: it
//                 is 10 rows tall and therefore most of what the eye sees. Handing
//                 both modes `.field-input` would put the SAME grey well inside both
//                 boxes in dark mode — two dark greys for the two message kinds is
//                 the exact failure §7.4 exists to prevent. So a note is typed on
//                 bg-note-strong in text-note-body (12.7:1 light, 8.9:1 dark) and a
//                 reply on bg-reply in text-ink. Warm vs cool, at full size.
//
// The separation is hue first and fill weight second, so it survives greyscale and
// colour blindness as well as it survives a dark page.

import MacroPicker from './MacroPicker.jsx';

export default function Composer({
  value,
  onChange,
  subjectRef,
  canSendCustomerEmail,
  macros,
  onApplyMacro,
  errors,
}) {
  const isNote = value.is_internal_note;

  function set(key, next) {
    onChange({ ...value, [key]: next });
  }

  return (
    <section className="rounded-lg border border-line bg-surface p-4">
      <div>
        <label className="field-label" htmlFor="ticket-subject">
          Subject <span className="text-red-600 dark:text-red-400">*</span>
        </label>
        <input
          id="ticket-subject"
          ref={subjectRef}
          className="field-input"
          autoComplete="off"
          aria-invalid={Boolean(errors?.subject)}
          aria-describedby={errors?.subject ? 'ticket-subject-error' : undefined}
          value={value.subject}
          onChange={(event) => set('subject', event.target.value)}
        />
        {errors?.subject && (
          <p id="ticket-subject-error" className="mt-1 text-xs text-red-700 dark:text-red-300">
            {errors.subject}
          </p>
        )}
      </div>

      <div className="mt-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div role="group" aria-label="Message type" className="flex gap-1">
            <button
              type="button"
              aria-pressed={isNote}
              onClick={() => set('is_internal_note', true)}
              className={`rounded-md px-2.5 py-1 text-xs font-semibold uppercase tracking-wide transition-colors ${
                isNote
                  ? 'bg-note-accent text-note-on-accent ring-1 ring-note-line'
                  : 'bg-sunken text-ink-secondary hover:bg-line hover:text-ink'
              }`}
            >
              Internal note
            </button>
            <button
              type="button"
              aria-pressed={!isNote}
              disabled={!canSendCustomerEmail}
              onClick={() => set('is_internal_note', false)}
              title={canSendCustomerEmail ? undefined : 'Your role cannot send customer-facing email.'}
              className={`rounded-md px-2.5 py-1 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                !isNote
                  ? 'bg-reply-strong text-reply-ink ring-1 ring-reply-accent'
                  : 'bg-sunken text-ink-secondary hover:bg-line hover:text-ink'
              }`}
            >
              Public reply
            </button>
          </div>

          <MacroPicker macros={macros} onApply={onApplyMacro} />
        </div>

        <div
          className={`mt-2 rounded-md border-l-8 p-2 ring-inset ${
            isNote
              ? 'border-note-accent bg-note ring-1 ring-note-line dark:ring-2'
              : 'border-reply-accent bg-reply-strong ring-1 ring-reply-accent'
          }`}
        >
          <label
            className={`mb-1 block text-xs font-semibold ${isNote ? 'text-note-ink' : 'text-reply-ink'}`}
            htmlFor="ticket-body"
          >
            {isNote
              ? 'Internal note: stays inside the team, never sent to the customer'
              : 'Public reply: emailed to the customer'}
          </label>
          <textarea
            id="ticket-body"
            rows={10}
            // Not `.field-input`: that class is bg-surface/dark:bg-sunken, which
            // would make the note and the reply the same grey well in dark mode over
            // the largest area on the screen (see the header note, SPEC.md §7.4).
            // The focus ring stays brand in both modes so focus means one thing
            // everywhere; a 2px ring does not out-shout an 8px bar and a filled card.
            className={`block w-full rounded-md border-0 px-3 py-2 text-sm ring-1 ring-inset
                        focus:ring-2 focus:ring-inset focus:ring-brand-600 dark:focus:ring-brand-400 ${
                          isNote
                            ? 'bg-note-strong text-note-body ring-note-line placeholder:text-note-ink'
                            : 'bg-reply text-ink ring-line-strong placeholder:text-ink-muted'
                        }`}
            placeholder={isNote ? 'What the caller reported, and what was done.' : 'What the customer will receive.'}
            value={value.body_text}
            onChange={(event) => set('body_text', event.target.value)}
          />
        </div>
      </div>
    </section>
  );
}
